'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  PackageIcon,
  SettingsIcon,
  ShoppingCartIcon,
  UsersIcon,
  XIcon,
  UserIcon,
  FileClockIcon,
  BookOpenCheckIcon,
  FileBoxIcon,
  CalendarCheckIcon,
  FlagIcon,
  NewspaperIcon,
  ImagesIcon,
  BellIcon,
  BookOpenTextIcon,
  BuildingIcon,
  FolderArchiveIcon,
  CoinsIcon,
  LayersIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AppUserDropdown } from '@/components/partials/layout'
import { useMeStore, useNotificationsStore } from '@/store'
import { useDashboard } from '@/hooks'
import { hasPermission } from '@/lib/permissions'
import { cn, textLimit } from '@/lib/utils'
import { MetaData } from '@/types/meta'
import logoDark from '@/assets/img/logo-white.svg'

export const menuItems = [
  {
    title: 'dashboard',
    icon: HomeIcon,
    href: '/',
    count: 0
  },
  {
    title: 'notifications',
    href: '/notifications',
    icon: BellIcon,
    count: 0
  },
  {
    title: 'products',
    icon: PackageIcon,
    href: '#',
    permission: { name: 'PRODUCTS', action: 'LIST' },
    count: 0,
    children: [
      { title: 'list', href: '/products', permission: { name: 'PRODUCTS', action: 'LIST' }, count: 0 },
      {
        title: 'categories',
        href: '/products/categories',
        permission: { name: 'PROPERTIES', action: 'LIST' },
        count: 0
      },
      {
        title: 'vehicle_groups',
        href: '/products/vehicle-groups',
        permission: { name: 'VEHICLE_GROUPS', action: 'LIST' },
        count: 0
      },
      {
        title: 'brands',
        href: '/products/brands',
        permission: { name: 'PRODUCTS', action: 'LIST' },
        count: 0
      },
      {
        title: 'models',
        href: '/products/models',
        permission: { name: 'PRODUCTS', action: 'LIST' },
        count: 0
      },
      {
        title: 'properties',
        href: '/products/properties',
        permission: { name: 'PROPERTIES', action: 'LIST' },
        count: 0
      }
    ]
  },
  {
    title: 'orders',
    icon: ShoppingCartIcon,
    href: '/orders',
    permission: { name: 'ORDERS', action: 'LIST' },
    count: 0
  },
  {
    title: 'blog_posts',
    icon: NewspaperIcon,
    href: '/blog-posts',
    permission: { name: 'BLOG_POSTS', action: 'LIST' },
    count: 0
  },
  {
    title: 'events',
    icon: CalendarCheckIcon,
    href: '/events',
    permission: { name: 'EVENTS', action: 'LIST' },
    count: 0
  },
  {
    title: 'pages',
    icon: BookOpenTextIcon,
    href: '/pages',
    permission: { name: 'PAGES', action: 'LIST' },
    count: 0
  },
  {
    title: 'production_stages',
    icon: LayersIcon,
    href: '/production-stages',
    permission: { name: 'PRODUCTION_STAGES', action: 'LIST' },
    count: 0
  },
  {
    title: 'contact_units',
    icon: BuildingIcon,
    href: '/contact-units',
    permission: { name: 'CONTACT_UNITS', action: 'LIST' },
    count: 0
  },
  {
    title: 'galleries',
    icon: ImagesIcon,
    href: '/galleries',
    count: 0,
    children: [
      {
        title: 'photo_galleries',
        href: '/galleries/photo-galleries',
        permission: { name: 'PHOTO_GALLERIES', action: 'LIST' },
        count: 0
      },
      { title: 'videos', href: '/galleries/videos', permission: { name: 'VIDEOS', action: 'LIST' }, count: 0 },
      { title: 'slides', href: '/galleries/slides', permission: { name: 'SLIDES', action: 'LIST' }, count: 0 },
      {
        title: 'certificates',
        href: '/galleries/certificates',
        permission: { name: 'CERTIFICATES', action: 'LIST' },
        count: 0
      },
      {
        title: 'catalogues',
        href: '/galleries/catalogues',
        permission: { name: 'CATALOGUES', action: 'LIST' },
        count: 0
      }
    ]
  },
  {
    title: 'files',
    icon: FileBoxIcon,
    href: '/files',
    permission: { name: 'FILES', action: 'LIST' },
    count: 0
  },
  {
    title: 'user_interactions',
    icon: BookOpenCheckIcon,
    href: '#',
    count: 0,
    children: [
      {
        title: 'contact_form_messages',
        href: '/user-interactions/contact-messages',
        permission: { name: 'CONTACT_FORM_MESSAGES', action: 'LIST' },
        count: 0
      },
      {
        title: 'job_applications',
        href: '/user-interactions/job-applications',
        permission: { name: 'JOB_APPLICATIONS', action: 'LIST' },
        count: 0
      }
    ]
  },
  {
    title: 'logs',
    icon: FileClockIcon,
    href: '#',
    count: 0,
    children: [
      { title: 'audit_logs', href: '/logs/audit', permission: { name: 'AUDIT_LOGS', action: 'LIST' }, count: 0 },
      { title: 'search_logs', href: '/logs/search', permission: { name: 'SEARCH_LOGS', action: 'LIST' }, count: 0 }
    ]
  },
  {
    title: 'users',
    icon: UsersIcon,
    href: '#',
    count: 0,
    children: [
      { title: 'list', href: '/users', permission: { name: 'USERS', action: 'LIST' }, count: 0 },
      { title: 'create', href: '/users/create', permission: { name: 'USERS', action: 'CREATE' }, count: 0 },
      { title: 'groups', href: '/users/groups', permission: { name: 'GROUPS', action: 'LIST' }, count: 0 }
    ]
  },
  {
    title: 'localization',
    icon: FlagIcon,
    href: '#',
    count: 0,
    children: [
      { title: 'locales', href: '/localization/locales', permission: { name: 'LOCALES', action: 'LIST' }, count: 0 },
      {
        title: 'i18n_messages',
        href: '/localization/i18n-messages',
        permission: { name: 'I18N_MESSAGES', action: 'LIST' },
        count: 0
      },
      {
        title: 'route_params',
        href: '/localization/route-params',
        permission: { name: 'ROUTE_PARAMS', action: 'LIST' },
        count: 0
      }
    ]
  },
  {
    title: 'currencies',
    icon: CoinsIcon,
    href: '/currencies',
    count: 0,
    permission: { name: 'CURRENCIES', action: 'LIST' }
  },
  {
    title: 'backups',
    icon: FolderArchiveIcon,
    href: '/backups',
    count: 0,
    permission: { name: 'BACKUPS', action: 'LIST' }
  },
  {
    title: 'settings',
    icon: SettingsIcon,
    href: '#',
    count: 0,
    children: [
      {
        title: 'general_settings',
        href: '/settings/general',
        permission: { name: 'SETTINGS', action: 'GENERAL_SHOW' },
        count: 0
      },
      {
        title: 'content_settings',
        href: '/settings/content',
        permission: { name: 'SETTINGS', action: 'CONTENT_SHOW' },
        count: 0
      },
      {
        title: 'system_settings',
        href: '/settings/system',
        permission: { name: 'SETTINGS', action: 'SYSTEM' },
        count: 0
      }
    ]
  }
]

type PropsType = {
  collapsed: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  isMobile: boolean
  metaData: MetaData
  locale: string
  disableTransition?: boolean
}

const AppSidebar = ({
  collapsed,
  open,
  onOpenChange,
  isMobile,
  metaData,
  locale,
  disableTransition = false
}: PropsType) => {
  const pathname = usePathname()
  const t = useTranslations()
  const [openMenus, setOpenMenus] = useState<string[]>([])
  const { meState } = useMeStore()
  const { unreadCount: unreadNotificationsCount } = useNotificationsStore()
  const { data: dashboardData } = useDashboard()
  const unreadContactFormMessagesCount = dashboardData?.stats?.unreadContactFormMessages ?? 0
  const unreadJobApplicationsCount = dashboardData?.stats?.unreadJobApplications ?? 0
  const pendingOrdersCount = dashboardData?.stats?.orders?.pending ?? 0

  const formatCount = (count: number) => (count > 9 ? '9+' : count)
  const currentUser = meState.response

  // Filter menu items based on user permissions
  const filterMenuItems = (items: typeof menuItems) => {
    return items
      .map(item => {
        // If an item has children, filter them first
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            // If child has permission requirement, check it
            if ('permission' in child && child.permission) {
              const { name: permissionName, action: actionName } = child.permission
              return hasPermission(currentUser, permissionName, actionName)
            }
            // If no permission requirement, show it
            return true
          })

          // If no children are accessible, hide a parent
          if (filteredChildren.length === 0) {
            return null
          }

          // Return parent with filtered children
          return { ...item, children: filteredChildren }
        }

        // If item has permission requirement, check it
        if ('permission' in item && item.permission) {
          const { name: permissionName, action: actionName } = item.permission
          if (!hasPermission(currentUser, permissionName, actionName)) {
            return null
          }
        }

        return item
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
  }

  const filteredMenuItems = filterMenuItems(menuItems)

  // Calculate the total count for menu items with children
  const menuItemsWithTotalCount = filteredMenuItems.map(item => {
    // Update notifications count dynamically
    if (item.title === 'notifications') {
      return { ...item, count: unreadNotificationsCount }
    }

    // Update pending orders count dynamically
    if (item.href === '/orders') {
      return { ...item, count: pendingOrdersCount }
    }

    if (item.children) {
      const childrenWithCounts = item.children.map(child => {
        if (child.href === '/user-interactions/contact-messages') {
          return { ...child, count: unreadContactFormMessagesCount }
        }
        if (child.href === '/user-interactions/job-applications') {
          return { ...child, count: unreadJobApplicationsCount }
        }
        return child
      })
      const totalChildCount = childrenWithCounts.reduce((sum, child) => sum + (child.count || 0), 0)
      return { ...item, children: childrenWithCounts, count: totalChildCount }
    }
    return item
  })

  // Keep track of the last pathname to detect actual navigation changes
  const [lastPathname, setLastPathname] = useState(pathname)

  // Load saved openMenus from localStorage on the component mount
  useEffect(() => {
    const savedOpenMenus = localStorage.getItem('sidebarOpenMenus')
    let initialMenus: string[] = []

    if (savedOpenMenus) {
      try {
        const parsedMenus = JSON.parse(savedOpenMenus)
        if (Array.isArray(parsedMenus)) {
          initialMenus = parsedMenus
        }
      } catch (error) {
        console.error('Error parsing saved sidebar menu state:', error)
      }
    }

    // If no saved menus, automatically open the active menu on an initial load
    if (initialMenus.length === 0) {
      const activeMenu = menuItemsWithTotalCount.find(
        item => item.children?.some(child => pathname.startsWith(child.href)) || pathname === item.href
      )

      if (activeMenu && activeMenu.children) {
        initialMenus = [activeMenu.title]
      }
    }

    setOpenMenus(initialMenus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Save openMenus to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpenMenus', JSON.stringify(openMenus))
  }, [openMenus])

  useEffect(() => {
    const activeMenu = menuItemsWithTotalCount.find(
      item => item.children?.some(child => pathname.startsWith(child.href)) || pathname === item.href
    )

    // Only update openMenus if this is a real navigation (pathname changed)
    const isPathChanged = lastPathname !== pathname
    setLastPathname(pathname)

    if (isPathChanged) {
      // Close mobile sidebar on navigation
      if (isMobile) {
        onOpenChange(false)
      }

      setOpenMenus(() => {
        // If there's an active menu with children, open it
        if (activeMenu && activeMenu.children) {
          // Close all other menus and keep only the active one
          return [activeMenu.title]
        }
        // If no active menu with children (e.g., dashboard), close all menus
        return []
      })
    }
  }, [lastPathname, menuItemsWithTotalCount, pathname, isMobile, onOpenChange])

  const toggleMenu = (title: string) => {
    if (collapsed && !isMobile) {
      return
    }
    setOpenMenus(prev => (prev.includes(title) ? prev.filter(item => item !== title) : [...prev, title]))
  }

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/'
    }
    if (href !== '#') {
      // Check if the current path matches or starts with the href
      return pathname === href || pathname.startsWith(href + '/')
    }
    return false
  }

  const isParentActive = (item: any) => {
    return item.children?.some((child: any) => pathname.startsWith(child.href)) || pathname === item.href
  }

  const sidebarClasses = cn(
    'app-sidebar-init h-screen bg-sidebar border-r border-sidebar-border shadow-lg z-50 flex flex-col overflow-hidden select-none',
    !disableTransition && 'transition-all duration-300 ease-in-out',
    isMobile
      ? `fixed left-0 top-0 w-72 transform ${open ? 'translate-x-0' : '-translate-x-full'}`
      : `fixed left-0 top-0 ${collapsed ? 'w-16' : 'w-72'}`
  )

  return (
    <div className={sidebarClasses} suppressHydrationWarning>
      <div className="border-sidebar-border flex h-16 shrink-0 items-center justify-start border-b p-4">
        <Link href="/" className="flex flex-1 items-center space-x-2">
          <div className="bg-sidebar-primary flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg">
            <Image src={logoDark} alt="" height={20} />
          </div>
          {(!collapsed || isMobile) && (
            <div className="min-w-0 flex-1">
              <h2 className="text-sidebar-foreground truncate text-sm font-semibold">
                {textLimit(metaData.contentSettings.title[locale], 25)}
              </h2>
              <p className="text-sidebar-foreground/70 truncate text-xs">{t('admin_dashboard')}</p>
            </div>
          )}
        </Link>

        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="hover:bg-sidebar-accent ml-2 h-8 w-8">
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 overflow-auto">
        <nav className="py-2">
          <div className="space-y-1 px-2">
            {menuItemsWithTotalCount.map(item => (
              <div key={item.title}>
                {item.children ? (
                  <div>
                    {collapsed && !isMobile ? (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <DropdownMenu>
                            <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className={cn(
                                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative w-full justify-start px-4 font-normal',
                                    isParentActive(item) && 'bg-sidebar-accent text-sidebar-accent-foreground'
                                  )}>
                                  <span className="shrink-0">
                                    <item.icon className="h-4 w-4" />
                                  </span>
                                  {item.count > 0 && (
                                    <Badge className="absolute top-0 right-0 flex h-3.5 w-3.5">
                                      {formatCount(item.count)}
                                    </Badge>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="right">{t(item.title)}</TooltipContent>

                            <DropdownMenuContent side="right" align="start" className="border-sidebar-border w-56">
                              <div className="mb-1 border-b border-gray-100 px-2 py-1.5 text-sm font-semibold text-gray-900 dark:border-gray-800 dark:text-gray-100">
                                {t(item.title)}
                              </div>
                              {item.children.map(child => (
                                <DropdownMenuItem key={`${item.title}-${child.title}`} asChild>
                                  <Link
                                    href={child.href}
                                    className={cn(
                                      'flex cursor-pointer items-center justify-between',
                                      isActive(child.href) &&
                                        'bg-gray-50 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400'
                                    )}>
                                    <span>{t(child.title)}</span>
                                    {child.count > 0 && <Badge className="text-xs">{formatCount(child.count)}</Badge>}
                                  </Link>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className={cn(
                            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground w-full items-center justify-start px-4 text-left font-normal',
                            isParentActive(item) && 'bg-sidebar-accent text-sidebar-accent-foreground'
                          )}
                          onClick={() => toggleMenu(item.title)}>
                          <span className="shrink-0">
                            <item.icon className="h-4 w-4" />
                          </span>
                          <span className="flex-1 truncate">{t(item.title)}</span>
                          {item.count > 0 && <Badge>{formatCount(item.count)}</Badge>}
                          <span className="shrink-0">
                            {openMenus.includes(item.title) ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </span>
                        </Button>

                        {openMenus.includes(item.title) && (
                          <div className="border-sidebar-border mt-1 ml-6 space-y-1 border-l pl-4">
                            {item.children.map(child => (
                              <Button
                                key={`${item.title}-${child.title}`}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent w-full justify-start text-left font-normal',
                                  isActive(child.href) && 'bg-sidebar-accent text-sidebar-accent-foreground'
                                )}
                                asChild>
                                <Link href={child.href} className="flex items-center justify-between px-2 py-1 text-sm">
                                  <span>{t(child.title)}</span>
                                  {child.count > 0 && <Badge className="text-xs">{formatCount(child.count)}</Badge>}
                                </Link>
                              </Button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground relative w-full justify-start px-4 text-left font-normal',
                            isActive(item.href) && 'bg-sidebar-accent text-sidebar-accent-foreground'
                          )}
                          asChild>
                          <Link href={item.href}>
                            <span className="shrink-0">
                              <item.icon className="h-4 w-4" />
                            </span>
                            <span
                              className={cn(
                                'flex-1 truncate overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-300 ease-in-out',
                                collapsed && !isMobile ? 'max-w-0 opacity-0' : 'max-w-full opacity-100'
                              )}>
                              {t(item.title)}
                            </span>
                            {(!collapsed || isMobile) && item.count > 0 && (
                              <Badge className="ml-auto text-xs">{formatCount(item.count)}</Badge>
                            )}
                            {collapsed && !isMobile && item.count > 0 && (
                              <Badge className="absolute top-0 right-0 flex h-3.5 w-3.5">
                                {formatCount(item.count)}
                              </Badge>
                            )}
                          </Link>
                        </Button>
                      </TooltipTrigger>
                      {collapsed && !isMobile && <TooltipContent side="right">{t(item.title)}</TooltipContent>}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            ))}
          </div>
        </nav>
      </ScrollArea>

      <div className="border-sidebar-border flex min-h-[61] shrink-0 items-center justify-center border-t p-2">
        <AppUserDropdown>
          {!collapsed || isMobile ? (
            <Button variant="ghost" className="h-auto w-full justify-start rounded-lg p-1">
              <div className="flex w-full items-center space-x-2">
                <div className="bg-sidebar-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                  <UserIcon className="text-sidebar-accent-foreground h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sidebar-foreground truncate text-sm font-medium">Admin User</p>
                  <p className="text-sidebar-foreground/70 truncate text-xs">admin@example.com</p>
                </div>
              </div>
            </Button>
          ) : (
            <Button variant="ghost" className="w-full justify-center rounded-lg p-1">
              <div className="bg-sidebar-accent flex h-8 w-8 items-center justify-center rounded-lg">
                <UserIcon className="text-sidebar-accent-foreground h-4 w-4" />
              </div>
            </Button>
          )}
        </AppUserDropdown>
      </div>
    </div>
  )
}

export default AppSidebar
